import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  corregirApertura,
  corregirCorte,
  datosDeLaCaja,
  dolaresEsperados,
  efectivoEsperado,
  lugarEnElLog,
  todoDelGeneral,
  type CorreccionDeConteo,
  type PorQueElConteoNo,
  type RechazoDeConteo,
} from '@abastece/core';
import { formatMoney, parseMoney, type Centavos } from '@abastece/shared';
import { db, ordenReplay } from '../db/dexie.js';
import { localReader, readSettings } from '../db/reader.js';
import { useT, type Clave } from '../i18n/index.js';
import { cuandoLargo } from '../lib/detalleEvento.js';
import { emitirYVer } from '../lib/emitir.js';
import { diceElConteo, loQueEncontro } from '../lib/encontrado.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { Campo } from './Campo.js';
import { enDolares } from './CuentasDeCaja.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** What stops a count from being corrected whatever is typed: said instead of the form. */
const NO_SE_CORRIGE: Partial<Record<PorQueElConteoNo, Clave>> = {
  deRuta: 'conteoCaja.no.deRuta',
  antigua: 'conteoCaja.no.antigua',
  entregada: 'conteoCaja.no.entregada',
  sinCorte: 'conteoCaja.no.sinCorte',
};

/** What stops what was typed, said under it. The same count again just leaves the button off. */
const ASI_NO: Partial<Record<PorQueElConteoNo, Clave>> = {
  quedaDeMas: 'conteoCaja.no.quedaDeMas',
  relevada: 'conteoCaja.no.relevada',
  recibida: 'conteoCaja.no.recibida',
};

/**
 * A till, and its counts as this phone's books took them: the opening, and the corte once it has
 * closed, and the opening of the shift that took the drawer over since, which moves with what the
 * corte left. What the books know of it to correct one (`datosDeLaCaja`), read the way the books
 * read it.
 */
async function leerLaCaja(sesionId: string) {
  const [sesion, config, sinEfecto] = await Promise.all([db.sesiones.get(sesionId), readSettings(), leerSinEfecto()]);
  if (!sesion) return null;
  const conteosDe = async (id: string, tipos: string[]) =>
    (
      await db.eventos
        .where('type')
        .anyOf(tipos)
        .filter((e) => (e.payload as { sesionId?: unknown } | null)?.sesionId === id)
        .toArray()
    )
      .filter((e) => tuvoEfecto(e, sinEfecto))
      .sort(ordenReplay);
  const conteos = await conteosDe(sesionId, ['caja.abierta', 'caja.cerrada']);
  const apertura = conteos.find((e) => e.type === 'caja.abierta') ?? null;
  const corte = conteos.find((e) => e.type === 'caja.cerrada') ?? null;
  const relevo = corte ? await localReader.relevoDe(sesionId) : undefined;
  const siguiente = relevo ? ((await conteosDe(relevo.id, ['caja.abierta']))[0] ?? null) : null;
  const caja = await datosDeLaCaja(
    localReader,
    config,
    sesion,
    apertura && { id: apertura.id, lugar: lugarEnElLog(apertura) },
    corte && { id: corte.id, lugar: lugarEnElLog(corte) },
    siguiente && { id: siguiente.id, lugar: lugarEnElLog(siguiente) },
  );
  const [quien, equipo] = await Promise.all([
    db.usuarios.get(sesion.cerradaPor ?? sesion.usuarioId),
    db.dispositivos.get(sesion.dispositivoId),
  ]);
  const abrio = await db.usuarios.get(sesion.usuarioId);
  return { sesion, config, caja, apertura, corte, abrio: abrio?.nombre ?? '', cerro: quien?.nombre ?? '', equipo: equipo?.nombre ?? '' };
}

/** An amount whole on its line: «C$» never apart from its figure. */
const entero = (texto: string) => texto.replace(/(C\$|US\$) /g, '$1\u00a0');

/** Córdobas, and dollars beside them when there are any. */
const enAmbas = (cordobas: number, dolares: number) =>
  dolares ? `${formatMoney(cordobas as Centavos)} + ${enDolares(dolares as Centavos)}` : formatMoney(cordobas as Centavos);

/**
 * A till's opening or corte counted wrong, put right by the owner (2026-10-14: «is there any way to
 * correct that till opening and till closing amount as the owner?»), from the shift's card in
 * «Turnos anteriores» and from the count's own record in «Bitácora». It says what was recorded,
 * takes what was really counted, and before saving says what the books will do, from the reading
 * they use (`corregirApertura`, `corregirCorte` in core): the float back to general cash or out of
 * it, what the opening and the corte found, what was handed over, what stays in the drawer. Once
 * the drawer has opened again, what the corte left moves the next opening with it (2026-10-15: «why
 * can't i correct "amount left in till"»), and that opening's sheet offers the corte it took over.
 */
export function CorregirConteo({ sesionId, cual, onCerrar }: { sesionId: string; cual: 'apertura' | 'corte'; onCerrar: () => void }) {
  const t = useT();
  const datos = useLiveQuery(() => leerLaCaja(sesionId), [sesionId]);
  const [cordobas, setCordobas] = useState<string | null>(null);
  const [dolares, setDolares] = useState<string | null>(null);
  const [queda, setQueda] = useState<string | null>(null);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alUltimoCorte, setAlUltimoCorte] = useState(false);
  const deApertura = cual === 'apertura';
  const titulo = t(deApertura ? 'conteoCaja.tituloApertura' : 'conteoCaja.tituloCorte');

  if (datos === undefined) return null;
  if (datos === null) {
    return (
      <Modal titulo={titulo} onCerrar={onCerrar}>
        <p className="text-sm text-slate-400">{t('registro.noEstaAqui')}</p>
      </Modal>
    );
  }

  const { sesion: s, caja, config } = datos;
  // What the last corte left is put right on that corte, and this opening moves with it.
  if (alUltimoCorte && s.relevoDe) return <CorregirConteo sesionId={s.relevoDe} cual="corte" onCerrar={onCerrar} />;
  const anotado = {
    cordobas: deApertura ? s.fondoInicial : (s.conteoCordobas ?? 0),
    dolares: deApertura ? (s.fondoDolares ?? 0) : (s.conteoDolares ?? 0),
    queda: s.queda ?? 0,
  };
  const texto = (escrito: string | null, valor: number) => escrito ?? (valor / 100).toFixed(2);
  const leido = {
    cordobas: parseMoney(texto(cordobas, anotado.cordobas)),
    dolares: parseMoney(texto(dolares, anotado.dolares)),
    queda: parseMoney(texto(queda, anotado.queda)),
  };
  const plan = (x: { cordobas: number; dolares: number; queda: number }): CorreccionDeConteo | RechazoDeConteo =>
    deApertura ? corregirApertura(caja, x, config) : corregirCorte(caja, x, config);
  // What holds whatever is typed shows with the count as it was recorded.
  const comoSeAnoto = plan(anotado);
  const definitivo = 'rechazo' in comoSeAnoto ? NO_SE_CORRIGE[comoSeAnoto.por] : undefined;
  const conteoListo = leido.cordobas !== null && leido.dolares !== null && (deApertura || leido.queda !== null);
  const resultado = conteoListo ? plan({ cordobas: leido.cordobas!, dolares: leido.dolares!, queda: leido.queda ?? 0 }) : null;
  const hecho = resultado && !('rechazo' in resultado) ? resultado : null;
  const asiNo = resultado && 'rechazo' in resultado ? resultado : null;
  const listo = hecho !== null && motivo.trim() !== '' && !ocupado;

  /** What a count found, before (past) or after (present) the correction. */
  const estado = (total: number, antes: boolean) => loQueEncontro(t, total, antes);
  const resultadoDe = (total: number) => {
    const monto = formatMoney(Math.abs(total) as Centavos);
    return total < 0 ? t('conteoCaja.faltaron', { monto }) : total > 0 ? t('conteoCaja.sobraron', { monto }) : t('conteoCaja.cuadro');
  };
  const todoDeGeneral = todoDelGeneral(s);
  const entregado = enAmbas(s.entregado ?? 0, s.entregadoDolares ?? 0);

  async function corregir() {
    if (!listo || !datos || !hecho) return;
    setOcupado(true);
    setError(null);
    try {
      const { apertura, corte } = datos;
      const { rechazo } = deApertura
        ? await emitirYVer('caja.apertura_corregida', {
            eventoId: apertura!.id,
            ...(s.estado === 'cerrada' && corte ? { corteId: corte.id } : {}),
            fondoInicial: leido.cordobas!,
            fondoDolares: leido.dolares!,
            motivo: motivo.trim(),
          })
        : await emitirYVer('caja.corte_corregido', {
            eventoId: corte!.id,
            ...(hecho.siguiente ? { aperturaSiguienteId: hecho.siguiente.aperturaId } : {}),
            conteoCordobas: leido.cordobas!,
            conteoDolares: leido.dolares!,
            queda: leido.queda!,
            motivo: motivo.trim(),
          });
      if (rechazo) {
        setOcupado(false);
        setError(rechazo);
        return;
      }
    } catch (e) {
      setOcupado(false);
      setError(e instanceof Error ? e.message : t('conteoCaja.noSePudo'));
      return;
    }
    onCerrar();
  }

  const libros = hecho ? lineasDeLosLibros(hecho) : [];
  function lineasDeLosLibros(c: CorreccionDeConteo): { clave: string; texto: string }[] {
    const lineas: { clave: string; texto: string }[] = [];
    if (c.apertura.antes !== c.apertura.despues) {
      lineas.push({ clave: 'apertura', texto: t('conteoCaja.alAbrir', { antes: estado(c.apertura.antes, true), despues: estado(c.apertura.despues, false) }) });
    }
    if (c.corte && c.corte.antes !== c.corte.despues) {
      lineas.push({ clave: 'corte', texto: t('conteoCaja.enElCorte', { antes: estado(c.corte.antes, true), despues: estado(c.corte.despues, false) }) });
    }
    if (c.siguiente && c.siguiente.antes !== c.siguiente.despues) {
      lineas.push({
        clave: 'siguiente',
        texto: t('conteoCaja.enLaSiguiente', { antes: estado(c.siguiente.antes, true), despues: estado(c.siguiente.despues, false) }),
      });
    }
    const general = (monto: number, como: (m: Centavos) => string, clave: string) => {
      if (monto > 0) lineas.push({ clave, texto: t('conteoCaja.vuelveAlGeneral', { monto: como(monto as Centavos) }) });
      if (monto < 0) lineas.push({ clave, texto: t('conteoCaja.saleDelGeneral', { monto: como(-monto as Centavos) }) });
    };
    general(c.aGeneral.cordobas, (m) => formatMoney(m), 'general');
    general(c.aGeneral.dolares, (m) => enDolares(m), 'generalDolares');
    // Counted since, general cash stays as counted, and what that count found moves instead.
    if (c.general) lineas.push({ clave: 'contado', texto: `${t('conteoCaja.generalContado')} ${diceElConteo(t, c.general)}` });
    const porRecibir = (monto: number, como: (m: Centavos) => string, clave: string) => {
      if (monto > 0) lineas.push({ clave, texto: t('conteoCaja.masPorRecibir', { monto: como(monto as Centavos) }) });
      if (monto < 0) lineas.push({ clave, texto: t('conteoCaja.menosPorRecibir', { monto: como(-monto as Centavos) }) });
    };
    porRecibir(c.aPorRecibir.cordobas, (m) => formatMoney(m), 'porRecibir');
    porRecibir(c.aPorRecibir.dolares, (m) => enDolares(m), 'porRecibirDolares');
    if (!deApertura && c.patch.queda != null && c.patch.queda !== (s.queda ?? 0)) {
      lineas.push({ clave: 'queda', texto: t('conteoCaja.quedaraEnGaveta', { monto: formatMoney(c.patch.queda) }) });
    }
    // Still open, its corte will expect what the drawer really started with.
    if (deApertura && s.estado === 'abierta') {
      const despues = { ...s, ...c.patch };
      lineas.push({ clave: 'esperara', texto: t('conteoCaja.esperara', { monto: enAmbas(efectivoEsperado(despues), dolaresEsperados(despues)) }) });
    }
    return lineas;
  }

  const filas: { clave: string; etiqueta: string; valor: string }[] = deApertura
    ? [
        { clave: 'conto', etiqueta: t('conteoCaja.seConto'), valor: enAmbas(anotado.cordobas, anotado.dolares) },
        ...(todoDeGeneral
          ? [{ clave: 'general', etiqueta: t('conteoCaja.delGeneral'), valor: enAmbas(s.deGeneral ?? 0, anotado.dolares) }]
          : [
              // Nothing left by a corte, only what was put in for change: no «Dejó el último corte C$ 0.00».
              ...((s.esperadoApertura ?? 0) > (s.deGeneral ?? 0)
                ? [{ clave: 'dejo', etiqueta: t('caja.dejoElCorte'), valor: formatMoney(((s.esperadoApertura ?? 0) - (s.deGeneral ?? 0)) as Centavos) }]
                : []),
              ...((s.deGeneral ?? 0) > 0 ? [{ clave: 'puesto', etiqueta: t('conteoCaja.puesto'), valor: formatMoney(s.deGeneral!) }] : []),
              { clave: 'resultado', etiqueta: t('conteoCaja.alAbrirFue'), valor: resultadoDe(s.diferenciaApertura ?? 0) },
            ]),
      ]
    : [
        { clave: 'conto', etiqueta: t('conteoCaja.seConto'), valor: enAmbas(anotado.cordobas, anotado.dolares) },
        { clave: 'esperaba', etiqueta: t('conteoCaja.seEsperaba'), valor: enAmbas(efectivoEsperado(s), dolaresEsperados(s)) },
        { clave: 'resultado', etiqueta: t('conteoCaja.resultado'), valor: resultadoDe(caja.totalCorte ?? 0) },
        { clave: 'queda', etiqueta: t('conteoCaja.seDejo'), valor: formatMoney(anotado.queda as Centavos) },
        {
          clave: 'entrego',
          etiqueta: t('conteoCaja.seEntrego'),
          valor: t(caja.destinoCorte === 'general' ? 'conteoCaja.alDueno' : 'conteoCaja.porRecibir', { monto: entregado }),
        },
        // Opened again since: what that opening found against what was left.
        ...(caja.siguiente
          ? [{ clave: 'siguiente', etiqueta: t('conteoCaja.laSiguiente'), valor: resultadoDe(caja.siguiente.sesion.diferenciaApertura ?? 0) }]
          : []),
      ];

  const quien = deApertura ? datos.abrio : datos.cerro;
  const cuando = deApertura ? s.abiertaEn : (s.cerradaEn ?? s.abiertaEn);

  return (
    <Modal
      titulo={titulo}
      onCerrar={onCerrar}
      pie={
        definitivo ? undefined : (
          <button type="button" className="btn-primario w-full" disabled={!listo} onClick={() => void corregir()} data-conteo-guardar>
            <Ico icono="guardar" />
            {t('conteoCaja.guardar')}
          </button>
        )
      }
    >
      <div className="flex flex-col gap-5" data-corregir-conteo={cual}>
        <p className="text-sm text-slate-400" data-conteo-de>
          {[quien, datos.equipo, cuandoLargo(cuando)].filter(Boolean).join(' · ')}
        </p>

        <section>
          <h3 className="rotulo">{t('conteoCaja.seAnoto')}</h3>
          <dl className="tarjeta flex flex-col gap-2 p-4" data-conteo-anotado>
            {filas.map((f) => (
              <div key={f.clave} className="flex items-baseline justify-between gap-4" data-conteo-fila={f.clave}>
                <dt className="text-sm text-slate-400">{f.etiqueta}</dt>
                <dd className="tabular text-right text-sm">{entero(f.valor)}</dd>
              </div>
            ))}
          </dl>
        </section>

        {deApertura && s.relevoDe && (
          <div className="flex flex-col items-start gap-1" data-conteo-ultimo-corte>
            <p className="text-sm text-slate-400">{t('conteoCaja.ultimoCorteAyuda')}</p>
            <button type="button" className="btn-texto gap-1 text-sm text-slate-300" onClick={() => setAlUltimoCorte(true)} data-conteo-corregir-ultimo>
              <Ico icono="editar" className="size-4" />
              {t('conteoCaja.corregirUltimoCorte')}
            </button>
          </div>
        )}

        {definitivo ? (
          <p className="text-sm text-amber-200" data-conteo-no-se-corrige>
            {t(definitivo)}
          </p>
        ) : (
          <>
            <p className="text-sm text-slate-400">{t(deApertura ? 'conteoCaja.ayudaApertura' : 'conteoCaja.ayudaCorte')}</p>
            <Campo
              etiqueta={t(deApertura ? 'conteoCaja.cordobasHabia' : 'conteoCaja.cordobasContados')}
              inputMode="decimal"
              className="tabular text-lg"
              value={texto(cordobas, anotado.cordobas)}
              onChange={(e) => {
                setCordobas(e.target.value);
                setError(null);
              }}
              data-conteo-cordobas
            />
            <Campo
              etiqueta={t(deApertura ? 'conteoCaja.dolaresHabia' : 'conteoCaja.dolaresContados')}
              inputMode="decimal"
              className="tabular"
              value={texto(dolares, anotado.dolares)}
              onChange={(e) => {
                setDolares(e.target.value);
                setError(null);
              }}
              data-conteo-dolares
            />
            {!deApertura && (
              <Campo
                etiqueta={t('conteoCaja.seDejo')}
                inputMode="decimal"
                className="tabular"
                value={texto(queda, anotado.queda)}
                ayuda={caja.relevada ? t('conteoCaja.yaSeAbrio') : undefined}
                onChange={(e) => {
                  setQueda(e.target.value);
                  setError(null);
                }}
                data-conteo-queda
              />
            )}
            {asiNo && (ASI_NO[asiNo.por] || asiNo.por === 'explicada') && (
              <p className="text-sm text-rose-400" data-conteo-no={asiNo.por}>
                {asiNo.por === 'explicada'
                  ? t('conteoCaja.no.explicada', {
                      explicado: formatMoney((asiNo.explicado ?? 0) as Centavos),
                      faltaria: formatMoney((asiNo.faltaria ?? 0) as Centavos),
                    })
                  : t(ASI_NO[asiNo.por]!)}
              </p>
            )}
            {libros.length > 0 && (
              <section className="rounded-lg bg-slate-800/60 px-3 py-2" data-conteo-libros>
                <h3 className="text-xs font-semibold text-slate-300">{t('conteoCaja.libros')}</h3>
                {libros.map((l) => (
                  <p key={l.clave} className="mt-1 text-xs text-slate-400" data-conteo-libro={l.clave}>
                    {entero(l.texto)}
                  </p>
                ))}
              </section>
            )}
            <Campo
              maxLength={200}
              etiqueta={t('comun.motivo')}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              autoComplete="off"
              data-conteo-motivo
            />
          </>
        )}

        {error && <p className="text-sm text-rose-400">{error}</p>}
      </div>
    </Modal>
  );
}
